import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { TileGridBlockData } from 'api/cms';
import { Box } from 'components/Box';
import { Button } from 'components/Button';
import { H2 } from 'components/Text';
import { colors, transition } from 'theme';

import { TileGridBlockTile } from './TileGridBlockTile';

type SectionButtonProps = { $active?: boolean };

const SectionButton = styled(Button).attrs<SectionButtonProps>(
  ({ $active }) => ({
    style: {
      color: $active ? colors.waterBlue : colors.battleshipGrey,
    },
  })
)<SectionButtonProps>`
  position: relative;
  &::before,
  &:after {
    content: '';
    position: absolute;
    bottom: -16px;
    left: 50%;
    transform: translateX(-50%);
    border: 0;
    opacity: 0;
    transition: all ${transition};
  }
  &::before {
    height: 10px;
    width: calc(100% - 48px);
    border-top: 2px solid currentColor;
  }
  &::after {
    border-color: transparent;
    border-style: solid;
    border-top-width: 5px;
    border-top-color: currentColor;
    border-left-width: 5px;
    border-right-width: 5px;
    border-bottom-width: 5px;
  }
  ${(props) =>
    props.$active
      ? `
    &::before, &::after {
      opacity: 1;
    }
  `
      : `
    &:focus, &:hover {
      &::before, &::after {
        opacity: 0.3;
      }
    }
  `}
`;

type Props = {
  blockIndex: number;
  data: TileGridBlockData;
};

export function TileGridBlock({
  blockIndex,
  data: { sections, imageSize },
}: Props) {
  const [currentTile, setCurrentTile] = useState(null);
  const [currentSection, setCurrentSection] = useState(0);
  useEffect(() => {
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setCurrentTile(null);
      }
    };

    document.addEventListener('keyup', onKeyUp);
    return () => {
      document.removeEventListener('keyup', onKeyUp);
    };
  }, []);
  return (
    <Box data-qa-id="cms-tile-grid">
      {sections.length > 1 && (
        <Box display="flex" flexWrap="wrap" justifyContent="center" mb={48}>
          {sections.map(({ name }, index) => (
            <SectionButton
              data-qa-id={`cms-tg-${name}`}
              key={name}
              px={24}
              my={16}
              borderLeft={index > 0 ? `1px solid ${colors.aluminum}` : '0'}
              $active={index === currentSection}
              onClick={() => setCurrentSection(index)}
            >
              <H2 my={0} styleAs="body">
                {name}
              </H2>
            </SectionButton>
          ))}
        </Box>
      )}
      {sections
        .filter((section, index) => index === currentSection)
        .map(({ name, tiles }) => (
          <Box key={name}>
            <Box display="flex" flexWrap="wrap" justifyContent="center">
              {tiles.map((tile, index) => (
                <Box
                  key={tile.heading}
                  display="flex"
                  justifyContent="center"
                  width={[1, 1 / 2, 1 / 4]}
                  maxWidth={400}
                  flex="0 0 auto"
                >
                  <TileGridBlockTile
                    tile={tile}
                    isOpen={index === currentTile}
                    isPage={false}
                    open={() => setCurrentTile(index)}
                    close={() => setCurrentTile(null)}
                    smallImage={imageSize === 'SMALL'}
                    blockIndex={blockIndex}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        ))}
    </Box>
  );
}
